import './style.css';

import React, { Fragment } from 'react';

const SIZE = window.innerWidth > 1280 ? 16 : window.innerWidth > 640 ? 14 : 12;
const BASE_RADIUS = SIZE / 2;
const BASE_FONT_SIZE = SIZE;

export function Component(className, styleSrc = {}, config = {}) {
  const { element = `div`, styleProps } = config;

  return function MakeComponent({ children, style = {}, ...props }) {
    const passProps = styleProps
      ? Object.keys(props)
          .filter((e) => !styleProps.includes(e))
          .reduce((acc, p) => {
            acc[p] = props[p];
            return acc;
          }, {})
      : props;

    const calculatedStyle = typeof styleSrc === 'function' ? styleSrc(props) : styleSrc;

    return React.createElement(
      element,
      {
        className,
        style: { ...style, ...calculatedStyle },
        ...passProps
      },
      children
    );
  };
}

export const Container = Component(`jbx-container`);

export const Text = Component(`jbx-text`);

export const SmallText = Component(`jbx-small-text`);

export const Button = Component(`jbx-button`, {}, { element: 'button' });

export const Input = Component(`jbx-input`, null, { element: 'input' });
export const Range = Component(`jbx-range`, null, { element: 'input' });

export const HeaderH1 = Component(`jbx-h${1}`, null, { element: 'h1' });
export const HeaderH2 = Component(`jbx-h${2}`, null, { element: 'h2' });
export const HeaderH3 = Component(`jbx-h${3}`, null, { element: 'h3' });
export const HeaderH4 = Component(`jbx-h${4}`, null, { element: 'h4' });

export const MainHeader = function MainHeader({ children, style }) {
  const content = String(children).split(' ');

  return React.createElement(
    Fragment,
    {},
    content.map((word) =>
      React.createElement(
        HeaderH1,
        {
          key: word,
          style: {
            fontWeight: 900,
            display: 'inline-block',
            width: 'auto',
            padding: '6px',
            margin: 0,
            backgroundColor: 'var(--accent-color)',
            ...style
          }
        },
        word
      )
    )
  );
};

export const A = Component(`jbx-a`, null, { element: 'a' });

export const Space = Component(`jbx-space`, (props) => ({
  display: props.inline ? 'inline-block' : 'block',
  height: `${SIZE * props.h}px`,
  width: `${SIZE * props.w}px`
}));

export const Box = Component(`jbx-box`, ({ padding, hideOnSmall, flex, minWidth }) => {
  return {
    flex,
    minWidth,
    padding: padding
      ? [padding]
          .flat()
          .map((paddingValue) => `${paddingValue * SIZE}px`)
          .join(` `)
      : null
  };
});

export const Inline = Component(`jbx-inline`, ({ v, h, gap }) => ({
  marginLeft: `${h * SIZE}px`,
  marginRight: `${h * SIZE}px`,
  marginTop: `${v * SIZE}px`,
  marginBottom: `${v * SIZE}px`,
  gap: `${gap * SIZE}px`
}));

export const Stack = Component(`jbx-stack`, ({ v, h, gap }) => ({
  marginLeft: `${h * SIZE}px`,
  marginRight: `${h * SIZE}px`,
  marginTop: `${v * SIZE}px`,
  marginBottom: `${v * SIZE}px`,
  gap: `${gap * SIZE}px`
}));

// export const LinkButton = Styled.a({
//   capHeight: BASE_FONT_SIZE,
//   color: '#3498db',
//   cursor: 'pointer',
//   borderRadius: 5,
//   borderTop: '1px solid #3498db10',
//   borderBottom: '1px solid #0000',
//   backgroundColor: '#3498db20',
//   height: BASE_FONT_SIZE * 2,
//   display: 'flex',
//   alignItems: 'center',
//   alignContent: 'center',
//   justifyContent: 'center',
//   padding: 0,
//   '&:hover': {
//     boxShadow: `rgba(0, 0, 0, 0.1) 0 1px 1px, rgba(0, 0, 0, 0.1) 0 3px 12px`
//   }
// });

export const Dropzone = Component(`jbx-dropzone`);

export const Ul = Component(
  `jbx-ul`,
  {
    margin: 0,
    padding: `0 0 0 ${BASE_FONT_SIZE / 4}px`
  },
  { element: 'ul' }
);

export const Li = Component(
  `jbx-li`,
  {
    margin: `0 0 0 ${SIZE}px`,
    padding: `${SIZE / 4}px 0`
  },
  { element: 'li' }
);

export const Tabs = Component(`jbx-tabs`);

export const Tab = Component(
  `jbx-tabs--tab`,
  ({ info, active }) => ({
    userSelect: 'none',
    cursor: 'pointer',
    backgroundColor: active ? '#000' : info ? '#fff' : '#ecf0f1',
    color: active ? '#fff' : '#000',
    padding: SIZE / 2,
    paddingLeft: info ? 0 : SIZE / 2
  }),
  { styleProps: ['info', 'active'] }
);

export const CodeSnippet = Component(`jbx-code-snippet`, null, { element: 'pre' });
export const CodeTextarea = Component(`jbx-code-area`, null, { element: 'textarea' });

export const CheckboxHidden = Component(`jbx-checkbox-hidden`, ({ checked }) => {
  return {
    height: SIZE * 2 - 4,
    width: SIZE * 2 - 4,
    paddingTop: 2,
    paddingLeft: 2,
    backgroundColor: checked ? 'var(--accent-color)' : 'white',
    borderRadius: BASE_RADIUS,
    boxShadow: checked
      ? 'inset rgba(0, 0, 0, 0.07) 0 0 0 1px'
      : 'inset rgba(0, 0, 0, 0.33) 0 0 0 1.5px',
    cursor: 'pointer'
  };
});

export const Checkbox = function Checkbox({ label, checked, onChange }) {
  return React.createElement(
    Inline,
    {
      onClick: () => {
        if (onChange) {
          onChange(!checked);
        }
      },
      style: {
        alignItems: 'center',
        cursor: 'pointer'
      }
    },
    [
      React.createElement(
        CheckboxHidden,
        {
          key: 0,
          checked
        },
        React.createElement(
          'svg',
          {
            xmlns: 'http://www.w3.org/2000/svg',
            width: SIZE * 2 - 8,
            height: SIZE * 2 - 8,
            fill: 'none',
            stroke: 'currentColor',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeWidth: '2',
            className: 'feather feather-check',
            viewBox: '0 0 24 24',
            color: checked ? 'white' : '#ddd'
          },
          React.createElement('path', {
            d: 'M20 6L9 17 4 12'
          })
        )
      ),
      React.createElement(Space, { key: 1, w: 0.5 }),
      React.createElement(Text, { key: 2, style: { userSelect: 'none' } }, label)
    ]
  );
};
